import type { EmblaCarouselType } from "embla-carousel";
import { useCallback, useEffect, useState } from "react";

export type EmblaScrollbarState = {
  showCue: boolean;
  progress: number;
  thumbWidth: number;
  thumbTranslateX: number;
};

/**
 * Normalizes scroll progress to a 0-1 range.
 * For looping carousels, wraps the progress value. For non-looping, clamps between 0 and 1.
 *
 * @param progress - Raw scroll progress value
 * @param loop - Whether the carousel is in loop mode
 * @returns Normalized progress value
 */
const normaliseProgress = (progress: number, loop: boolean) => {
  return loop ? ((progress % 1) + 1) % 1 : Math.min(1, Math.max(0, progress));
};

/**
 * Hook that manages scrollbar state for an Embla carousel.
 * Calculates thumb width, position, and scroll progress based on the carousel's scroll state.
 *
 * @param emblaApi - The Embla carousel API instance
 * @param scrollbarContainerRef - Optional ref to the scrollbar container element for margin calculations
 * @returns Object containing scrollbar state (progress, thumbWidth, thumbTranslateX, showCue)
 */
export function useEmblaScrollbar({
  emblaApi,
  scrollbarContainerRef
}: {
  emblaApi?: EmblaCarouselType;
  scrollbarContainerRef?: React.RefObject<HTMLDivElement>;
} = {}): EmblaScrollbarState {
  const [state, setState] = useState<EmblaScrollbarState>({
    showCue: false,
    progress: 0,
    thumbWidth: 0,
    thumbTranslateX: 0
  });

  const updateScrollbar = useCallback(() => {
    if (!emblaApi) return;

    // Account for container margins to ensure accurate scrollbar positioning
    const scrollbarContainer = scrollbarContainerRef?.current;
    let marginLeft = 0;
    let marginRight = 0;
    if (scrollbarContainer) {
      const computed = window.getComputedStyle(scrollbarContainer);
      marginLeft = parseFloat(computed.marginLeft) || 0;
      marginRight = parseFloat(computed.marginRight) || 0;
    }

    const viewport = emblaApi.rootNode();
    const viewportWidth = viewport.clientWidth;
    const adjustedViewportWidth = viewportWidth - (marginLeft + marginRight);

    const contentWidth = emblaApi.containerNode().scrollWidth;

    const rawProgress = emblaApi.scrollProgress();
    const loop = emblaApi.internalEngine().options.loop;
    const progress = normaliseProgress(rawProgress, loop);

    // Calculate thumb size based on viewport-to-content ratio
    // Minimum 5% width ensures thumb remains visible and usable
    const ratio = contentWidth > 0 ? adjustedViewportWidth / contentWidth : 1;
    const thumbRatio = Math.max(0.05, Math.min(1, ratio));
    const thumbWidth = thumbRatio * adjustedViewportWidth;

    // Calculate how far the thumb can travel, then position it based on scroll progress
    const travelPx = Math.max(adjustedViewportWidth - thumbWidth, 0);
    const thumbTranslateX = progress * travelPx;

    const canScroll =
      !!emblaApi && (emblaApi.canScrollNext() || emblaApi.canScrollPrev());

    setState({
      progress,
      thumbWidth,
      thumbTranslateX,
      showCue: canScroll
    });
  }, [emblaApi, scrollbarContainerRef]);

  useEffect(() => {
    if (!emblaApi) return;

    updateScrollbar();

    emblaApi.on("scroll", updateScrollbar);
    emblaApi.on("reInit", updateScrollbar);

    return () => {
      emblaApi.off("scroll", updateScrollbar);
      emblaApi.off("reInit", updateScrollbar);
    };
  }, [emblaApi, updateScrollbar]);

  return state;
}
